import { useEffect } from 'react';
import { logger } from '@parrot-software/observability';
import type { InvoiceDataViewDto } from '@subscriptions/application/dtos';
import { InvoiceDataDeepLinkModes } from '@subscriptions/presentation/constants';
import { useInvoiceDataByUuid } from '@subscriptions/presentation/hooks/useInvoiceDataByUuid';
import { useInvoiceDataSheetParams } from '@subscriptions/presentation/hooks/useInvoiceDataSheetParams';

const invoiceLog = logger.forDomain('SUB', { subdomain: 'billing' });

const KNOWN_MODES: string[] = [InvoiceDataDeepLinkModes.EDIT, InvoiceDataDeepLinkModes.RETRY];

interface UseInvoiceDataDeepLinkParams {
  activeUuid?: string | null;
  invoiceData?: InvoiceDataViewDto[];
  isEnabled?: boolean;
  onEdit: (invoiceData: InvoiceDataViewDto) => void;
  onRetry?: (invoiceData: InvoiceDataViewDto, errorMessage?: string | null) => void;
}

/**
 * Opens the fiscal data form sheet from a shareable URL — the inbound half of the
 * sheet/url sync, with the outbound half living in useInvoiceDataActions.
 *
 * Reads `?invoiceDataUuid=<uuid>` and, once the invoice data list has loaded, resolves the
 * matching record and hands it to `onEdit` — the same entry point the card's edit button
 * uses, so the sheet opens prefilled in edit mode.
 *
 * The optional `?invoiceDataMode=` param selects the flow: `edit` is the default when the
 * param is absent, and `retry` routes to `onRetry` so the sheet opens in retry mode (same
 * fields and update mutation as edit, different submit label). Unknown mode values are
 * ignored and open nothing.
 *
 * `?invoiceError=` carries the payment failure reason for retry links and is handed to
 * `onRetry` so the sheet can show why the charge failed.
 *
 * A retry target missing from the listing is read on its own, deleted records included:
 * correcting fiscal data by replacing the record rather than editing it orphans the link.
 *
 * `activeUuid` is the record the sheet is already showing. Re-opening it would push a new
 * object into the form, whose reset-on-initialValues effect would wipe whatever the user
 * has typed — so a target that is already open is skipped. Without this guard any refetch
 * of the list (a new array identity) would silently discard in-progress edits.
 *
 * Params are left in place on success: the url is meant to describe what is on screen, and
 * the close handlers clear it. They are only stripped when the link cannot be honoured — a
 * stale or mistyped uuid, an unknown mode, or `isEnabled` false because editing is
 * unavailable for inactive customers and a deep link must not bypass that guard.
 *
 * @param {Object} params - Hook parameters
 * @param {string} [params.activeUuid] - Uuid the sheet is currently open for, if any
 * @param {InvoiceDataViewDto[]} [params.invoiceData] - Loaded invoice data list, undefined while fetching
 * @param {boolean} [params.isEnabled] - Whether opening the sheet is allowed (defaults to true)
 * @param {Function} params.onEdit - Opens the sheet in edit mode for the given record
 * @param {Function} [params.onRetry] - Opens the sheet in retry mode; falls back to onEdit
 * @returns {void}
 *
 * @example
 * useInvoiceDataDeepLink({ activeUuid: editingInvoiceData?.uuid, invoiceData, onEdit: handleEdit });
 * // /subscriptions/blocks/<blockUuid>?tab=invoicing&invoiceDataUuid=<invoiceDataUuid>
 */
export const useInvoiceDataDeepLink = ({
  activeUuid,
  invoiceData,
  isEnabled = true,
  onEdit,
  onRetry,
}: UseInvoiceDataDeepLinkParams): void => {
  const { clearTarget, errorMessage, mode, paymentUuid, targetUuid } = useInvoiceDataSheetParams();

  const isModeSupported = mode === null || KNOWN_MODES.includes(mode);
  const listed = targetUuid && invoiceData ? invoiceData.find((item) => item.uuid === targetUuid) : undefined;

  // A retry link names the record its payment was invoiced with, and that record may have been
  // deleted since — correcting fiscal data by replacing the record is what orphans the link.
  // The listing hides deleted records, so this one is read on its own instead of dropping it.
  const isReplacedRetryTarget =
    Boolean(targetUuid) &&
    Boolean(invoiceData) &&
    !listed &&
    isEnabled &&
    isModeSupported &&
    mode === InvoiceDataDeepLinkModes.RETRY;

  const { data: replaced, isFetching: isFetchingReplaced } = useInvoiceDataByUuid(targetUuid, {
    enabled: isReplacedRetryTarget,
  });

  useEffect(() => {
    if (!targetUuid || !invoiceData) return;
    if (targetUuid === activeUuid) return;
    if (isReplacedRetryTarget && isFetchingReplaced) return;

    const target = listed ?? replaced;

    if (!isEnabled || !isModeSupported || !target) {
      // The link is dropped without a trace on screen, so this is the only record it existed.
      invoiceLog.warn('resolveInvoiceDataDeepLink', {
        'invoice.dataUuid': targetUuid,
        'invoice.paymentUuid': paymentUuid ?? undefined,
        'invoice.mode': mode ?? undefined,
        'invoice.discardReason': !isEnabled
          ? 'client_disabled'
          : !isModeSupported
          ? 'unknown_mode'
          : 'invoice_data_not_found',
      });
      clearTarget();
      return;
    }

    if (mode === InvoiceDataDeepLinkModes.RETRY && onRetry) {
      onRetry(target, errorMessage);
      return;
    }

    onEdit(target);
  }, [
    activeUuid,
    clearTarget,
    errorMessage,
    invoiceData,
    isEnabled,
    isFetchingReplaced,
    isModeSupported,
    isReplacedRetryTarget,
    listed,
    mode,
    onEdit,
    onRetry,
    paymentUuid,
    replaced,
    targetUuid,
  ]);
};
